"use client";

import { formatCurrency } from "@/app/(general-header)/botb-rewards/utils";
import { publishModalClasses, publishModalData, publishModalVisibility } from "@/app/redux/features/modal/modalSlice";
import { useAppDispatch } from "@/app/redux/hooks";
import SavingsInfoModal from "./SavingsInfoModal";

interface SavingsWidgetProps {
  totalSavings: number;
  currentSavings: number;
  currentPeriod: string;
  loading?: boolean;
}

export default function SavingsWidget({
  totalSavings,
  currentSavings,
  currentPeriod,
  loading = false,
}: SavingsWidgetProps) {
  const dispatch = useAppDispatch();

  const openSavingsInfo = () => {
    dispatch(publishModalData({ modalData: <SavingsInfoModal /> }));
    dispatch(publishModalClasses({ classNames: "lg:w-[602px] relative overflow-y-auto lg:rounded-2xl" }));
    dispatch(publishModalVisibility({ isOpen: true }));
  };

  if (totalSavings === 0) {
    return (
      <div className="w-full rounded-xl overflow-hidden">
        <div className="backdrop-blur-[2px] lg:backdrop-blur-[3.5px] bg-[rgba(245,245,245,0.5)] lg:bg-[rgba(245,245,245,0.2)] border border-black/[0.08] px-6 lg:px-14 py-3 flex flex-col items-center gap-1 rounded-xl">
          <p className="m-0 p-0 tracking-[0] text-[24px] lg:text-[32px] font-bold font-jost leading-7.5 lg:leading-10">
            🚀
          </p>
          <p className="m-0 p-0 tracking-[0] text-dark-1 text-[11px] leading-[14px] lg:text-base font-medium font-jost lg:leading-5.5 text-center">
            You just started your BOTB Pass membership!
          </p>
          <p className="m-0 p-0 tracking-[0] text-gray-1 text-[11px] leading-[14px] lg:text-base font-medium font-jost lg:leading-5.5 text-center">
            Your savings will appear here at the end of your first subscription cycle.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="w-full rounded-xl overflow-hidden">
        <div className="backdrop-blur-[2px] lg:backdrop-blur-[3.5px] bg-[rgba(245,245,245,0.5)] lg:bg-[rgba(245,245,245,0.2)] border border-black/[0.08] px-4 lg:px-2.5 py-3 flex flex-col items-center gap-1 rounded-t-xl">
          <p className="m-0 p-0 tracking-[0] text-dark-1 text-[11px] lg:text-sm font-medium font-jost leading-3.5 lg:leading-5 text-center whitespace-nowrap">
            Your total savings as a BOTB Pass Member
          </p>
          <p
            className={`m-0 p-0 tracking-[0] bg-gradient-botb bg-clip-text text-transparent text-[24px] lg:text-[28px] font-bold font-jost leading-7.5 lg:leading-8 whitespace-nowrap ${loading ? "opacity-30 blur-[5px]" : ""}`}>
            {formatCurrency(totalSavings)}
          </p>
          <p className="m-0 p-0 tracking-[0] text-gray-1 text-[11px] lg:text-sm font-medium font-jost leading-3.5 lg:leading-5 text-center whitespace-nowrap">
            Saved vs. paying full ticket price
          </p>
        </div>

        <div className="flex items-center justify-between lg:justify-center gap-[6px] lg:gap-4 pl-2.5 lg:px-4 pr-2 lg:pr-4 py-[9px] lg:py-2 rounded-b-xl bg-[linear-gradient(136deg,#1500ff_2%,#7366ff_101.5%)]">
          <p
            className={`m-0 p-0 tracking-[0] text-white text-xs lg:text-base font-normal lg:font-medium font-jost leading-4 lg:leading-5.5 whitespace-nowrap ${loading ? "opacity-30 blur-[5px]" : ""}`}>
            {currentSavings === 0 ? (
              <span className="animate-pulse-fade">Current savings building...</span>
            ) : (
              <>
                Current savings: <span className="font-medium lg:font-bold">{formatCurrency(currentSavings)}</span>
              </>
            )}
          </p>
          {currentPeriod && (
            <div className={`bg-white/20 rounded-full px-2.5 py-0.5 ${loading ? "opacity-30 blur-[5px]" : ""}`}>
              <p className="m-0 p-0 tracking-[0] text-white text-[10px] lg:text-sm font-medium font-jost leading-3 lg:leading-5 whitespace-nowrap">
                {currentPeriod}
              </p>
            </div>
          )}
        </div>
      </div>

      <p className="m-0 p-0 tracking-[0] text-[11px] leading-[14px] lg:text-sm font-normal font-jost text-left">
        <span className="text-[#898994]">
          Current savings are added to your total at the end of each subscription cycle.{" "}
        </span>
        <button onClick={openSavingsInfo} className="text-secondary underline cursor-pointer">
          How does this work?
        </button>
      </p>
    </div>
  );
}
